/**
 * Times on a site's wall clock (its IANA zone), for Warehouse records and dialogs: a ship time is
 * the From site's clock and an arrival the To site's. Pure: no React, storage or network. A zone the
 * browser does not know falls back to the viewer's own.
 */

const zoneCache = new Map<string, string | undefined>();

/** The IANA zone when the browser knows it; otherwise undefined (the viewer's own zone). */
function validZone(timeZone: string | null | undefined): string | undefined {
  if (!timeZone) return undefined;
  if (zoneCache.has(timeZone)) return zoneCache.get(timeZone);
  let valid: string | undefined;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    valid = timeZone;
  } catch {
    valid = undefined;
  }
  zoneCache.set(timeZone, valid);
  return valid;
}

function parseInstant(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

type ZonedParts = { year: number; month: number; day: number; hour: number; minute: number };

function zonedParts(date: Date, timeZone: string | undefined): ZonedParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  return { year: read('year'), month: read('month'), day: read('day'), hour: read('hour') % 24, minute: read('minute') };
}

function dayKey(parts: ZonedParts): string {
  return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
}

/** Calendar days from today to the instant's day, both in the site's zone (0 = today). */
export function siteDaysFromToday(iso: string | null | undefined, timeZone: string | null | undefined, now: Date = new Date()): number | null {
  const date = parseInstant(iso);
  if (!date) return null;
  const zone = validZone(timeZone);
  const target = Date.parse(`${dayKey(zonedParts(date, zone))}T00:00:00Z`);
  const today = Date.parse(`${dayKey(zonedParts(now, zone))}T00:00:00Z`);
  return Math.round((target - today) / 86_400_000);
}

/** "Oct 6" (with the year when it is not this year), in the site's zone. */
export function formatSiteDay(iso: string | null | undefined, timeZone: string | null | undefined, now: Date = new Date()): string | null {
  const date = parseInstant(iso);
  if (!date) return null;
  const zone = validZone(timeZone);
  const sameYear = zonedParts(date, zone).year === zonedParts(now, zone).year;
  return date.toLocaleDateString('en-US', { timeZone: zone, month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
}

/** "4:10 PM" in the site's zone. */
export function formatSiteClock(iso: string | null | undefined, timeZone: string | null | undefined): string | null {
  const date = parseInstant(iso);
  if (!date) return null;
  return date.toLocaleTimeString('en-US', { timeZone: validZone(timeZone), hour: 'numeric', minute: '2-digit' });
}

/** "Oct 6, 4:10 PM" in the site's zone. */
export function formatSiteDateTime(iso: string | null | undefined, timeZone: string | null | undefined, now: Date = new Date()): string | null {
  const day = formatSiteDay(iso, timeZone, now);
  const clock = formatSiteClock(iso, timeZone);
  return day && clock ? `${day}, ${clock}` : day;
}

/** "Today, 10:00 AM", "Tomorrow, 9:00 AM", "Yesterday, 4:10 PM" or "Oct 9, 4:00 PM". */
export function formatSiteMoment(iso: string | null | undefined, timeZone: string | null | undefined, now: Date = new Date()): string | null {
  const days = siteDaysFromToday(iso, timeZone, now);
  if (days == null) return null;
  const clock = formatSiteClock(iso, timeZone);
  const word = days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : days === -1 ? 'Yesterday' : null;
  if (word) return clock ? `${word}, ${clock}` : word;
  return formatSiteDateTime(iso, timeZone, now);
}

/** "today by 10:00 AM", "tomorrow by 9:00 AM" or "Oct 9 by 4:00 PM" (an expected time). */
export function formatSiteDeadline(iso: string | null | undefined, timeZone: string | null | undefined, now: Date = new Date()): string | null {
  const day = formatSiteDayWord(iso, timeZone, now);
  if (!day) return null;
  const clock = formatSiteClock(iso, timeZone);
  return clock ? `${day} by ${clock}` : day;
}

/** "today", "tomorrow" or "Oct 9" for running text. */
export function formatSiteDayWord(iso: string | null | undefined, timeZone: string | null | undefined, now: Date = new Date()): string | null {
  const days = siteDaysFromToday(iso, timeZone, now);
  if (days == null) return null;
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return formatSiteDay(iso, timeZone, now);
}

/** "EDT" for a site's zone at a moment (for date fields). */
export function siteZoneAbbreviation(timeZone: string | null | undefined, at: Date = new Date()): string | null {
  const zone = validZone(timeZone);
  if (!zone) return null;
  const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
    .formatToParts(at)
    .find((entry) => entry.type === 'timeZoneName');
  return part?.value ?? null;
}

/** A timestamp as a `datetime-local` value on the site's wall clock. */
export function toSiteInputValue(iso: string | null | undefined, timeZone: string | null | undefined): string {
  const date = parseInstant(iso);
  if (!date) return '';
  const parts = zonedParts(date, validZone(timeZone));
  return `${dayKey(parts)}T${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`;
}

const INPUT_VALUE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

/** A `datetime-local` value on the site's wall clock as an ISO timestamp (null when empty or invalid). */
export function fromSiteInputValue(value: string, timeZone: string | null | undefined): string | null {
  const match = value.trim().match(INPUT_VALUE);
  if (!match) return null;
  const [year, month, day, hour, minute] = match.slice(1, 6).map(Number);
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  const zone = validZone(timeZone);
  // Two passes settle the zone's offset, including across a DST change.
  let instant = wall;
  for (let pass = 0; pass < 2; pass += 1) {
    const parts = zonedParts(new Date(instant), zone);
    const seen = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
    instant = wall - (seen - instant);
  }
  return new Date(instant).toISOString();
}
